import React from "react";
import { MapPin } from "lucide-react";

// Stylized map of South Africa with all 9 provinces.
// Limpopo highlighted; Tzaneen marker placed in eastern Limpopo.
// Path data is a simplified illustrative outline (not survey-accurate).
const provinces = [
  {
    id: "limpopo",
    name: "Limpopo",
    highlight: true,
    d: "M 320 60 L 470 50 L 520 110 L 510 180 L 430 200 L 360 180 L 320 130 Z",
  },
  { id: "mpumalanga", name: "Mpumalanga", d: "M 430 200 L 510 180 L 540 230 L 520 290 L 440 290 L 410 240 Z" },
  { id: "gauteng", name: "Gauteng", d: "M 360 180 L 430 200 L 410 240 L 360 240 Z" },
  { id: "north-west", name: "North West", d: "M 220 140 L 320 130 L 360 180 L 360 240 L 280 250 L 220 220 Z" },
  { id: "free-state", name: "Free State", d: "M 280 250 L 360 240 L 410 240 L 440 290 L 410 350 L 320 360 L 280 320 Z" },
  { id: "kwazulu-natal", name: "KwaZulu-Natal", d: "M 440 290 L 520 290 L 560 360 L 510 410 L 440 380 L 410 350 Z" },
  { id: "eastern-cape", name: "Eastern Cape", d: "M 320 360 L 410 350 L 440 380 L 480 450 L 380 470 L 290 430 Z" },
  { id: "northern-cape", name: "Northern Cape", d: "M 80 180 L 220 140 L 220 220 L 280 250 L 280 320 L 220 360 L 130 340 L 80 280 Z" },
  { id: "western-cape", name: "Western Cape", d: "M 80 280 L 130 340 L 220 360 L 290 430 L 240 470 L 130 460 L 80 400 Z" },
];

const tzaneenMarker = { x: 470, y: 130 };
const johannesburgMarker = { x: 380, y: 215 };

export default function SouthAfricaMap() {
  return (
    <div className="w-full h-full flex flex-col">
      <svg viewBox="50 30 540 470" className="w-full h-full" data-testid="south-africa-map">
        <defs>
          <pattern id="grid" width="20" height="20" patternUnits="userSpaceOnUse">
            <path d="M 20 0 L 0 0 0 20" fill="none" stroke="#f1f5f9" strokeWidth="0.5" />
          </pattern>
          <radialGradient id="markerGlow" cx="50%" cy="50%" r="50%">
            <stop offset="0%" stopColor="#B45309" stopOpacity="0.6" />
            <stop offset="100%" stopColor="#B45309" stopOpacity="0" />
          </radialGradient>
        </defs>
        <rect x="50" y="30" width="540" height="470" fill="url(#grid)" />

        {/* Provinces */}
        {provinces.map((p) => (
          <path
            key={p.id}
            d={p.d}
            fill={p.highlight ? "#FEF3C7" : "#F8FAFC"}
            stroke={p.highlight ? "#B45309" : "#CBD5E1"}
            strokeWidth={p.highlight ? 2 : 1}
            className="transition-colors hover:fill-slate-100"
          >
            <title>{p.name}</title>
          </path>
        ))}

        {/* Lesotho enclave */}
        <ellipse cx="430" cy="350" rx="28" ry="20" fill="#FFFFFF" stroke="#94A3B8" strokeWidth="1" strokeDasharray="2 2" />
        <text x="430" y="354" textAnchor="middle" fontSize="9" fill="#64748b" fontWeight="500">Lesotho</text>

        {/* Province labels */}
        {provinces.map((p) => {
          const path = p.d.split(/[ML\s,]+/).filter(Boolean).map(Number);
          let cx = 0, cy = 0, n = 0;
          for (let i = 0; i < path.length - 1; i += 2) {
            cx += path[i]; cy += path[i + 1]; n++;
          }
          cx /= n; cy /= n;
          return (
            <text key={p.id} x={cx} y={cy} textAnchor="middle" fontSize="10"
              fill={p.highlight ? "#92400E" : "#64748b"}
              fontWeight={p.highlight ? "700" : "500"}
              className="font-display"
              style={{ pointerEvents: "none" }}
            >
              {p.name}
            </text>
          );
        })}

        {/* Johannesburg reference */}
        <circle cx={johannesburgMarker.x} cy={johannesburgMarker.y} r="3" fill="#64748b" />
        <text x={johannesburgMarker.x + 6} y={johannesburgMarker.y - 4} fontSize="9" fill="#64748b" fontWeight="600">
          Johannesburg
        </text>

        {/* Tzaneen marker glow + pin */}
        <circle cx={tzaneenMarker.x} cy={tzaneenMarker.y} r="28" fill="url(#markerGlow)" />
        <circle cx={tzaneenMarker.x} cy={tzaneenMarker.y} r="14" fill="none" stroke="#B45309" strokeWidth="1.5" opacity="0.5">
          <animate attributeName="r" from="14" to="26" dur="2s" repeatCount="indefinite" />
          <animate attributeName="opacity" from="0.7" to="0" dur="2s" repeatCount="indefinite" />
        </circle>
        <circle cx={tzaneenMarker.x} cy={tzaneenMarker.y} r="6" fill="#B45309" stroke="#fff" strokeWidth="2" />

        {/* Connector + label */}
        <line x1={tzaneenMarker.x + 6} y1={tzaneenMarker.y - 4} x2={tzaneenMarker.x + 50} y2={tzaneenMarker.y - 30}
          stroke="#0F172A" strokeWidth="1" />
        <g transform={`translate(${tzaneenMarker.x + 50}, ${tzaneenMarker.y - 50})`}>
          <rect width="120" height="36" rx="4" fill="#0F172A" />
          <text x="8" y="14" fontSize="9" fill="#FCD34D" fontWeight="700"
            style={{ letterSpacing: "0.1em" }}>AREA OF INTEREST</text>
          <text x="8" y="28" fontSize="11" fill="#FFFFFF" fontWeight="600" className="font-display">
            Tzaneen · Limpopo
          </text>
        </g>
      </svg>

      <div className="grid grid-cols-3 gap-3 mt-3 pt-3 border-t border-slate-100">
        <div className="text-center">
          <div className="text-[10px] uppercase tracking-wider text-slate-500">Lat / Lng</div>
          <div className="text-xs font-mono text-slate-800 mt-0.5">23.83°S 30.16°E</div>
        </div>
        <div className="text-center">
          <div className="text-[10px] uppercase tracking-wider text-slate-500">Province</div>
          <div className="text-xs font-semibold text-amber-700 mt-0.5">Limpopo · Mopani</div>
        </div>
        <div className="text-center">
          <div className="text-[10px] uppercase tracking-wider text-slate-500">Nearest Port</div>
          <div className="text-xs text-slate-800 mt-0.5">Maputo · 480 km</div>
        </div>
      </div>
    </div>
  );
}
